import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { polarizationTranslator } from 'components/features/HeatmapViewAndExportModal/utils/helpers';
import moment from 'moment-timezone';
import classnames from 'classnames';
import './RasterHeatmapMetaDataBox.css';
import { DEFAULT_METADATA_OBJ } from 'components/features/HeatmapViewAndExportModal/utils/constants';
import { updateSingleDevicePageSelectedBeamRankIdx } from 'redux/actions';
import { getUserTag } from 'common/utils/helpers';

const RasterHeatmapMetaDataBox = (props) => {
  const dispatch = useDispatch();
  const selectedDeviceHeatmap = props.selectedDeviceHeatmapRecord ?? {};
  const selectedBeamRankIndex = useSelector((store) => store.ui.singleDevicePage.heatmap.selectedBeamRankIndex);

  const leftInfoObj = {
    dateAndTime: {
      name: 'Date/Time',
      value: props.placeholder
        ? 'Unknown'
        : moment(
            selectedDeviceHeatmap?.ts?.['$date'] ? selectedDeviceHeatmap?.ts?.['$date'] : selectedDeviceHeatmap?.ts
          )?.format('MM/DD/YYYY,HH:mm') ?? 'Unknown',
      tooltip: props.placeholder
        ? 'Unknown'
        : moment(
            selectedDeviceHeatmap?.ts?.['$date'] ? selectedDeviceHeatmap?.ts?.['$date'] : selectedDeviceHeatmap?.ts
          )?.format('MM/DD/YYYY,HH:mm') ?? 'Unknown'
    },
    source: {
      name: 'Source',
      value: selectedDeviceHeatmap?.source == 'commissioning' ? 'comm' : selectedDeviceHeatmap?.source ?? 'Unknown',
      tooltip: selectedDeviceHeatmap?.source ?? 'Unknown'
    },
    user: {
      name: 'User',
      value: props.placeholder ? 'Unknown' : getUserTag(selectedDeviceHeatmap?.user?.name),
      tooltip: props.placeholder ? 'Unknown' : getUserTag(selectedDeviceHeatmap?.user?.name)
    },
    deviceId: {
      name: 'Device ID',
      value: selectedDeviceHeatmap?.deviceId ?? 'Unknown',
      tooltip: selectedDeviceHeatmap?.deviceId ?? 'Unknown'
    },
    polarization: {
      name: 'Pol',
      value: polarizationTranslator(selectedDeviceHeatmap?.beamrank?.[0]?.polarity).charAt(0) ?? 'N/A',
      tooltip: polarizationTranslator(selectedDeviceHeatmap?.beamrank?.[0]?.polarity) ?? 'N/A'
    },
    pci: {
      name: 'PCI',
      value: selectedDeviceHeatmap?.beamrank?.[0]?.['5g-kpis']?.[0]?.pci ?? 'N/A',
      tooltip: selectedDeviceHeatmap?.beamrank?.[0]?.['5g-kpis']?.[0]?.pci ?? 'N/A'
    },
    ssb: {
      name: 'SSBARFCN',
      value: selectedDeviceHeatmap?.beamrank?.[0]?.['5g-kpis']?.length > 0 ? selectedDeviceHeatmap?.beamrank?.[0]?.['ssb-arfcn'] : 'N/A',
      tooltip: selectedDeviceHeatmap?.beamrank?.[0]?.['5g-kpis']?.length > 0 ? selectedDeviceHeatmap?.beamrank?.[0]?.['ssb-arfcn'] : 'N/A'
    },
    scs: {
      name: 'SCS',
      value: 'N/A', //here we are not getting the value for the SCS, key SCS missing.
      tooltip: 'N/A' //here we are not getting the value for the SCS, key SCS missing.
    }
  };

  const handleBeamRankSelection = (index) => {
    dispatch(updateSingleDevicePageSelectedBeamRankIdx(index !== selectedBeamRankIndex ? index : 0));
  };

  let beamRank = props?.beamrank?.length > 0 ? props.beamrank : DEFAULT_METADATA_OBJ;
  //  Deafult object added for the custom row
  const customRowObj = {
    az: null,
    el: null,
    pci: null,
    rsrp: null,
    rsrq: null,
    rssi: null
  };

  if (beamRank.length === 3) {
    beamRank.push(customRowObj);
  }

  const containerClassName = props.forMaintHeatmap
    ? 'RasterHeatmap_MetaData_Box_Container_ViewExport'
    : 'RasterHeatmap_MetaData_Box_Container';

  const leftInfoContainerClassName = props.forMaintHeatmap
    ? 'RasterHeatmap_MetaData_Box_LeftInfo_Container_ViewExports'
    : 'RasterHeatmap_MetaData_Box_LeftInfo_Container';

  const infoRowClassName = props.forMaintHeatmap
    ? 'RasterHeatmap_MetaData_Box_LeftInfo_Row_ViewExport'
    : 'RasterHeatmap_MetaData_Box_LeftInfo_Row';

  const infoRowNameClassName = props.forMaintHeatmap
    ? 'RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport'
    : 'RasterHeatmap_MetaData_Box_LeftInfo_Row_Name';

  return (
    <div id={containerClassName} className={containerClassName}>
      <div id={leftInfoContainerClassName} className={leftInfoContainerClassName} style={{ display: 'flex' }}>
        {Object.keys(leftInfoObj).map((item, idx) => {
          return (
            <table className="infoTable" key={idx}>
              {/* header */}
              <thead style={{ background: 'lightgray' }}>
                <tr key={idx}>
                  <th
                    id={infoRowNameClassName}
                    className={infoRowNameClassName}
                    data-tip={leftInfoObj[item].name}
                    style={{ border: '1px solid' }}
                  >
                    {leftInfoObj[item].name}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr key={idx}>
                  {/* body */}
                  <td
                    style={{ cursor: 'default' }}
                    id="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value"
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling"
                    data-tip={leftInfoObj[item].tooltip}
                  >
                    {leftInfoObj[item].value}
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })}
      </div>
      {/* Updated the code in Tabular form and also added few classes for the table */}
      <div>
        {beamRank && beamRank.length > 0 ? (
          <table className="beam-rank-table" style={{ marginTop: '4px' }}>
            <thead style={{ background: 'lightgray' }}>
              <tr>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  Rank
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  Az/El
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  SSB Index
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  RSSI (dBm)
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  SS-RSRP (dBm)
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  SS-RSRQ (dB)
                </th>
                <th className={`${infoRowNameClassName} RasterHeatmap_MetaData_Box_LeftInfo_Row_Name_ViewExport raster-heatmap-table-styling`}>
                  SS-SINR (dB)
                </th>
              </tr>
            </thead>
            <tbody>
              {beamRank.map((item, idx) => (
                <tr key={idx}>
                  <td
                    className={classnames({
                      RasterHeatmap_MetaData_Box_RightInfo_Row_Name: true,
                      RasterHeatmap_MetaData_Box_RightInfo_Row_Name_Selected:
                        selectedBeamRankIndex === idx + 1 && props?.beamrank
                    })}
                    onClick={() => props?.beamrank && handleBeamRankSelection(idx + 1)}
                    data-tip={'Click to display/hide details'}
                    style={{ border: '1px solid', padding: '2px', textAlign: 'center' }}
                  >
                    {' '}
                    {idx === 3 ? 'Custom' : `${idx + 1}`}
                  </td>

                  <td className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling default-pointer">
                    <span data-tip={`${item.az ?? 'N/A'} degrees`}>{item.az ?? 'N/A'}</span>, {''}
                    <span data-tip={`${item.el ?? 'N/A'} degrees`}>{item.el ?? 'N/A'}</span>
                  </td>
                  <td
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling default-pointer"
                    data-tip={`${item.ssbIndex ?? 'N/A'}`}
                  >
                    {item.ssbIndex ?? 'N/A'}
                  </td>
                  <td
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling"
                    data-tip={`${item.rssi ?? 'N/A'}`}
                  >
                    {item.rssi ?? 'N/A'}
                  </td>
                  <td
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling"
                    data-tip={`${item.ssbRsrp ?? 'N/A'}`}
                  >
                    {item.ssbRsrp ?? 'N/A'}
                  </td>
                  <td
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling"
                    data-tip={`${item.ssbRsrq ?? 'N/A'}`}
                  >
                    {item.ssbRsrq ?? 'N/A'}
                  </td>
                  <td
                    className="RasterHeatmap_MetaData_Box_LeftInfo_Row_Value raster-heatmap-table-styling"
                    data-tip={`${item.ssbSinr ?? 'N/A'}`}
                  >
                    {item.ssbSinr ?? 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            id="RasterHeatmap_MetaData_Box_RightInfo_Row_MetaData_Col_No_BeamInfo"
            className="RasterHeatmap_MetaData_Box_RightInfo_Row_MetaData_Col_No_BeamInfo"
          >
            No beam information is available
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(RasterHeatmapMetaDataBox);
